import type { GoogleIdPConnection, GoogleIdPSyncResult, GoogleIdPSyncRun } from '#dn-types/google-idp.ts';
import { FetchError, MissingResourceError, rewordValidationMessages } from './errors.ts';
import { del, get, post } from './fetch.ts';

// GET /v1/google-idp
/** `null` when the organization has not connected a directory. */
export type GetGoogleIdPConnectionResult = GoogleIdPConnection | null;
export function getGoogleIdPConnection(init?: RequestInit): Promise<{ data: GetGoogleIdPConnectionResult }> {
  return get<GoogleIdPConnection>('/v1/google-idp', init).catch((err: unknown) => {
    // Not being connected is an answer, not an error.
    if (err instanceof MissingResourceError) {
      return { data: null };
    }
    throw err;
  });
}

// GET /v1/google-idp/providers
/** An endpoint SSO provider that signs users in with Google, which a Google Workspace directory can be connected from. */
export type GoogleIdPProvider = { id: string };
export function getGoogleIdPProviders(init?: RequestInit) {
  return get<GoogleIdPProvider[]>('/v1/google-idp/providers', init);
}

// POST /v1/google-idp/start
export type StartGoogleIdPVariables = { endpointOIDCProviderID: string };
export type StartGoogleIdPResult = {
  /** Google's consent screen, where the admin is sent next */
  redirectURL: string;
  /** Kept by the browser until Google sends the admin back, then handed to verify */
  requestToken: string;
};
export function startGoogleIdP(values: StartGoogleIdPVariables) {
  return post<StartGoogleIdPResult>('/v1/google-idp/start', values).catch((err: unknown) => {
    rewordGoogleIdPErrors(err);
  });
}

// POST /v1/google-idp/verify
export type VerifyGoogleIdPVariables = {
  /** The full callback URL Google redirected to, carrying `code` (or `error`) and `state` */
  location: string;
  requestToken: string;
};
export type VerifyGoogleIdPResult = GoogleIdPConnection & { endpointOIDCProviderID: string };
export function verifyGoogleIdP(values: VerifyGoogleIdPVariables) {
  return post<VerifyGoogleIdPResult>('/v1/google-idp/verify', values).catch((err: unknown) => {
    rewordGoogleIdPErrors(err);
  });
}

// POST /v1/google-idp/test
export type TestGoogleIdPResult = GoogleIdPConnection;
export function testGoogleIdP() {
  return post<TestGoogleIdPResult>('/v1/google-idp/test').catch((err: unknown) => {
    rewordGoogleIdPErrors(err);
  });
}

// POST /v1/google-idp/sync/dry-run, POST /v1/google-idp/sync
export type StartGoogleIdPSyncVariables = {
  /** Overrides the automatic deprovisioning limit for this one run, after an admin has reviewed the users */
  maxDeprovisions?: number;
};
function startGoogleIdPSync(apply: boolean, values: StartGoogleIdPSyncVariables) {
  return post<GoogleIdPSyncRun>(apply ? '/v1/google-idp/sync' : '/v1/google-idp/sync/dry-run', values);
}

// GET /v1/google-idp/sync/runs/:id
export function getGoogleIdPSyncRun(id: string, init?: RequestInit) {
  return get<GoogleIdPSyncRun>(`/v1/google-idp/sync/runs/${id}`, init);
}

/** A sync run that ended without a result to show. */
export class GoogleIdPSyncRunError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'GoogleIdPSyncRunError';
    this.code = code;
  }
}

const SYNC_POLL_INTERVAL_MS = 1000;

/**
 * {@link runGoogleIdPSync} starts a sync, or with `apply: false` its dry run, and polls it until it finishes. It
 * resolves with the run's result, or rejects with a {@link GoogleIdPSyncRunError} when the run ended without one.
 */
export async function runGoogleIdPSync({
  apply,
  ...values
}: StartGoogleIdPSyncVariables & { apply: boolean }): Promise<GoogleIdPSyncResult> {
  let run: GoogleIdPSyncRun;
  try {
    run = await startGoogleIdPSync(apply, values);
  } catch (err) {
    // Mutations only parse 400 bodies, so the one 409 here is told apart by status.
    if (err instanceof FetchError && err.response.status === 409) {
      const code = 'ERR_GOOGLE_IDP_SYNC_IN_PROGRESS';
      throw new GoogleIdPSyncRunError(code, googleIdPErrorMessage(code) ?? 'A sync is already running.');
    }
    rewordGoogleIdPErrors(err);
  }

  while (run.status === 'running') {
    await new Promise((resolve) => setTimeout(resolve, SYNC_POLL_INTERVAL_MS));
    ({ data: run } = await getGoogleIdPSyncRun(run.id));
  }

  if (run.status === 'done' && run.result) {
    return run.result;
  }

  const code = run.error?.code ?? 'ERR_INTERNAL_SERVER_ERROR';
  throw new GoogleIdPSyncRunError(code, googleIdPErrorMessage(code) ?? 'The sync did not finish. Please try again.');
}

// DELETE /v1/google-idp
export type DeleteGoogleIdPResult = Record<string, never>;
export function deleteGoogleIdP() {
  return del<DeleteGoogleIdPResult>('/v1/google-idp');
}

function rewordGoogleIdPErrors(err: unknown): never {
  rewordValidationMessages(err, (details) => googleIdPErrorMessage(details.code));
}

// Codes that also arrive as bare statuses, which describeGoogleIdPError maps to the same sentences.
const DIRECTORY_UNAVAILABLE_MESSAGE = 'We couldn’t reach Google. Please try again in a moment.';
const NOT_CONNECTED_MESSAGE = 'No Google Workspace directory is connected. Reload the page and try again.';
const NOT_CONFIGURED_MESSAGE =
  'Google directory sync is not available right now. Please try again later, or contact support.';

/** The admin-facing sentence for a google-idp error code, or undefined for a code without one. */
function googleIdPErrorMessage(code: string): string | undefined {
  switch (code) {
    case 'ERR_GOOGLE_IDP_PROVIDER_NOT_GOOGLE':
      return 'This provider does not sign users in with Google, so a Google directory cannot be connected to it.';
    case 'ERR_INVALID_REFERENCE':
      return 'This provider no longer exists. Reload the page and try again.';
    case 'ERR_GOOGLE_IDP_INVALID_REQUEST_TOKEN':
      return 'This connection attempt expired or was already used. Start again from the provider page.';
    case 'ERR_GOOGLE_IDP_CONSENT_DENIED':
      return 'Access was not granted on Google’s consent screen, so nothing was connected.';
    case 'ERR_GOOGLE_IDP_MISSING_SCOPES':
      return 'Some required permissions were unchecked on Google’s consent screen. Try again and leave them all checked.';
    case 'ERR_GOOGLE_IDP_MISSING_REFRESH_TOKEN':
      return 'Google did not issue a long-lived credential because an earlier grant is still active. Remove Defined Networking from your Google account’s third-party access page, then try again.';
    case 'ERR_GOOGLE_IDP_NOT_ADMIN':
      return 'The Google account you used cannot read the directory. Connect with a Google Workspace administrator account.';
    case 'ERR_GOOGLE_IDP_NOT_SUPER_ADMIN':
      return 'The Google account that connected the directory is not a Google Workspace super admin. Disconnect it and connect again with a super admin account.';
    case 'ERR_GOOGLE_IDP_NOT_CONNECTED':
      return NOT_CONNECTED_MESSAGE;
    case 'ERR_GOOGLE_IDP_NOT_CONFIGURED':
      return NOT_CONFIGURED_MESSAGE;
    case 'ERR_GOOGLE_IDP_EXCHANGE':
      return 'We couldn’t complete the connection with Google. Please try again.';
    case 'ERR_GOOGLE_IDP_GRANT_INVALID':
      return 'Google no longer accepts this connection. Disconnect it and connect again.';
    case 'ERR_GOOGLE_IDP_DIRECTORY_UNAVAILABLE':
      return DIRECTORY_UNAVAILABLE_MESSAGE;
    case 'ERR_GOOGLE_IDP_SYNC_IN_PROGRESS':
      return 'A sync of this directory is already running. Wait for it to finish, then try again.';
    case 'ERR_GOOGLE_IDP_SYNC_INTERRUPTED':
      return 'The sync stopped before it finished, most likely because our servers restarted. Hosts it already blocked stay blocked; run it again to finish.';
    default:
      return undefined;
  }
}

/**
 * {@link describeGoogleIdPError} turns any error from the google-idp endpoints into a sentence for the admin.
 * 400s are reworded above; the rest arrive without a parsed body, so they are told apart by status.
 */
export function describeGoogleIdPError(err: unknown): string {
  if (err instanceof GoogleIdPSyncRunError) {
    return err.message;
  }
  if (err instanceof FetchError) {
    const [first] = err.errors;
    if (first && 'reworded' in first && first.reworded) {
      return first.message;
    }

    switch (err.response.status) {
      case 404:
        return NOT_CONNECTED_MESSAGE;
      case 409:
        return 'A Google directory is already connected to your organization.';
      case 502:
        return DIRECTORY_UNAVAILABLE_MESSAGE;
      case 503:
        return NOT_CONFIGURED_MESSAGE;
      default:
        break;
    }
  }

  return 'Something went wrong talking to Google. Please try again, or contact support.';
}
